import Phaser from 'phaser';
import { createGame, stepGame, type Direction, type GameState, BIOMES } from '@neon-maw/shared';
import { installDebugApi } from '../game/debugApi';
import { loadSettings, GameSettings } from '../game/settings';

const TILE = 32;

interface Trail {
  x: number;
  y: number;
  alpha: number;
  scale: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: number;
  size: number;
  shape: 'square' | 'circle';
}

export class MainScene extends Phaser.Scene {
  private state!: GameState;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private lastMove = 0;

  private mapGraphics!: Phaser.GameObjects.Graphics;
  private entityGraphics!: Phaser.GameObjects.Graphics;
  private fxGraphics!: Phaser.GameObjects.Graphics;

  private cameraTarget!: Phaser.GameObjects.Rectangle;
  private bumpFeedback = 0;
  private bumpDir: Direction = 'none';
  private hitStopTicks = 0;

  private trails: Trail[] = [];
  private particles: Particle[] = [];

  private initOptions: any;
  private minimap!: Phaser.Cameras.Scene2D.Camera;
  private settings!: GameSettings;

  constructor() { super('main'); }

  init(data: any) {
    this.initOptions = data;
    this.settings = loadSettings();
  }

  create(): void {
    this.state = createGame(this.initOptions || 1);
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.input.keyboard!.addKey('SPACE');

    // Layers
    this.mapGraphics = this.add.graphics();
    this.fxGraphics = this.add.graphics();
    this.entityGraphics = this.add.graphics();

    // Camera target
    this.cameraTarget = this.add.rectangle(0, 0, 1, 1, 0x000000, 0);
    this.cameras.main.startFollow(this.cameraTarget, true, 0.1, 0.1);
    this.cameras.main.setZoom(1.5);

    // Minimap
    this.minimap = this.cameras.add(this.scale.width - 220, 20, 200, 200).setZoom(0.15).setName('mini');
    this.minimap.setBackgroundColor(0x000000);
    this.minimap.scrollX = (this.state.width * TILE) / 2;
    this.minimap.scrollY = (this.state.height * TILE) / 2;

    this.scene.launch('ui', { main: this });

    this.drawMap();
    this.redraw();

    installDebugApi(() => this.state, (dir: Direction) => { this.move(dir); });
  }

  update(time: number, delta: number): void {
    if (this.state && (this.state.runStatus === 'won' || this.state.runStatus === 'lost')) {
      this.scene.stop('ui');
      this.scene.start('reward', { state: this.state, sessionId: this.initOptions?.sessionId, seed: this.initOptions?.seed });
      return;
    }

    if (this.hitStopTicks > 0) {
      this.hitStopTicks -= delta;
      return; // Freeze everything
    }

    let needsRedrawFx = false;

    if (this.bumpFeedback > 0) {
      this.bumpFeedback = Math.max(0, this.bumpFeedback - delta);
      this.drawMap(); // Needs redraw for the wall flash effect
      needsRedrawFx = true;
    }

    // Update trails
    for (let i = this.trails.length - 1; i >= 0; i--) {
      this.trails[i].alpha -= delta * 0.003;
      if (this.trails[i].alpha <= 0) {
        this.trails.splice(i, 1);
      }
      needsRedrawFx = true;
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * delta * 0.05;
      p.y += p.vy * delta * 0.05;
      p.vx *= Math.pow(0.95, delta / 16.6); // drag
      p.vy *= Math.pow(0.95, delta / 16.6);
      p.life -= delta;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
      needsRedrawFx = true;
    }

    if (needsRedrawFx) {
      this.redraw();
    }

    if (time - this.lastMove < 110) return;
    
    let dir: Direction = 'none';
    const keys = this.settings.keyBindings;
    const k = this.input.keyboard!;
    const isDown = (code: string, cursorsProp: keyof Phaser.Types.Input.Keyboard.CursorKeys) => {
      const phaserKey = k.keys[Phaser.Input.Keyboard.KeyCodes[code as keyof typeof Phaser.Input.Keyboard.KeyCodes]] || k.addKey(code);
      return (this.cursors[cursorsProp] && this.cursors[cursorsProp]!.isDown) || (phaserKey && phaserKey.isDown);
    };

    if (isDown(keys.left, 'left')) dir = 'left';
    else if (isDown(keys.right, 'right')) dir = 'right';
    else if (isDown(keys.up, 'up')) dir = 'up';
    else if (isDown(keys.down, 'down')) dir = 'down';
    
    if (dir !== 'none') {
      this.lastMove = time;
      this.move(dir);
    }
  }

  private audioCtx?: AudioContext;

  private playSoundHook(event: string) {
    console.log(`[Audio Event Hook] ${event}`);
    
    // Lazy init audio context on first sound (requires user interaction first)
    if (!this.audioCtx) {
      try {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (e) {
        return;
      }
    }
    
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    const t = this.audioCtx.currentTime;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    
    osc.connect(gain);
    gain.connect(this.audioCtx.destination);
    
    if (event === 'eat_dot') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(1200, t + 0.05);
      gain.gain.setValueAtTime(0.1, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
      osc.start(t);
      osc.stop(t + 0.05);
    } else if (event === 'eat_power') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(400, t);
      osc.frequency.linearRampToValueAtTime(800, t + 0.1);
      osc.frequency.linearRampToValueAtTime(600, t + 0.2);
      gain.gain.setValueAtTime(0.15, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.2);
    } else if (event === 'player_hit') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150, t);
      osc.frequency.exponentialRampToValueAtTime(40, t + 0.3);
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.3);
    } else if (event === 'ghost_eaten') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(100, t + 0.2);
      gain.gain.setValueAtTime(0.1, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.2);
    } else {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, t);
      gain.gain.setValueAtTime(0.1, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.1);
      osc.start(t);
      osc.stop(t + 0.1);
    }
  }

  private emitParticles(x: number, y: number, color: number, count: number, speedMult = 1, sizeBase = 4) {
    if (this.settings.reduceMotion) count = Math.floor(count / 2); // reduce particles if reduced motion
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * 3 + 1) * speedMult;
      const maxL = 300 + Math.random() * 400;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: maxL,
        maxLife: maxL,
        color,
        size: sizeBase + Math.random() * sizeBase,
        shape: Math.random() > 0.5 ? 'circle' : 'square'
      });
    }
  }

  private triggerScreenShake(intensity: number, duration: number) {
    if (this.settings.reduceMotion) return;
    this.cameras.main.shake(duration, intensity);
  }

  private processLogs(prevLogLength: number) {
    const newLogs = this.state.log.slice(prevLogLength);
    for (const log of newLogs) {
      const px = this.state.player.pos.x * TILE + TILE / 2;
      const py = this.state.player.pos.y * TILE + TILE / 2;

      if (log === 'eat_dot') {
        this.playSoundHook('eat_dot');
        this.emitParticles(px, py, 0xffffff, 4, 0.5, 2);
      } else if (log === 'eat_power') {
        this.playSoundHook('eat_power');
        this.emitParticles(px, py, 0xffff00, 15, 1.5, 3);
        this.hitStopTicks = 40;
        this.triggerScreenShake(0.005, 80);
      } else if (log.startsWith('hit:')) {
        this.playSoundHook('player_hit');
        this.hitStopTicks = 120;
        this.triggerScreenShake(0.01, 100);
        this.emitParticles(px, py, 0xff0000, 25, 2.0, 4);
      } else if (log.startsWith('banished:')) {
        this.playSoundHook('ghost_eaten');
        this.hitStopTicks = 80;
        this.triggerScreenShake(0.01, 100);
        this.emitParticles(px, py, 0x00ffff, 20, 1.8, 3);
      }
    }
  }

  private move(dir: Direction) {
    const prevState = this.state;
    const prevLogLen = prevState.log.length;
    const prevPx = prevState.player.pos.x;
    const prevPy = prevState.player.pos.y;

    this.state = stepGame(this.state, {dir});

    if (this.state.player.pos.x === prevPx && this.state.player.pos.y === prevPy) {
       this.bumpFeedback = 100;
       this.bumpDir = dir;
       if (dir !== 'none') {
         this.playSoundHook('bump_wall');
         this.triggerScreenShake(0.002, 40); // micro-shake on wall bump
       }
    } else {
       this.bumpFeedback = 0;
       
       // Dash trail logic
       if (!this.settings.reduceMotion) {
         this.trails.push({
           x: prevPx * TILE + TILE/2,
           y: prevPy * TILE + TILE/2,
           alpha: 0.5,
           scale: 1.0
         });
       }
    }

    this.processLogs(prevLogLen);

    this.drawMap();
    this.redraw();
  }

  public getState(): GameState {
    return this.state;
  }

  public applySettings(settings: GameSettings) {
    this.settings = settings;
    this.drawMap(); // redraw to apply high contrast if needed
  }

  private drawMap(): void {
    this.mapGraphics.clear();
    
    const w = this.state.width;
    const h = this.state.height;
    
    const biome = this.state.biomeId ? BIOMES[this.state.biomeId as keyof typeof BIOMES] : BIOMES['tutorial'];
    const bgNum = parseInt(biome.palette.background.replace('#', '0x'), 16);
    this.mapGraphics.fillStyle(bgNum, 1);
    this.mapGraphics.fillRect(0, 0, w * TILE, h * TILE);
    
    this.mapGraphics.lineStyle(1, 0x111121, 0.5);
    for (let x=0; x<=w; x++) { this.mapGraphics.beginPath(); this.mapGraphics.moveTo(x*TILE, 0); this.mapGraphics.lineTo(x*TILE, h*TILE); this.mapGraphics.strokePath(); }
    for (let y=0; y<=h; y++) { this.mapGraphics.beginPath(); this.mapGraphics.moveTo(0, y*TILE); this.mapGraphics.lineTo(w*TILE, y*TILE); this.mapGraphics.strokePath(); }

    for (let y=0; y<h; y++) {
      for (let x=0; x<w; x++) {
        const t = this.state.tiles[y*w+x];
        const px = x*TILE;
        const py = y*TILE;
        
        if (t === '#') { 
          let isBumped = false;
          if (this.bumpFeedback > 0) {
             const bx = this.state.player.pos.x + (this.bumpDir==='left'? -1 : this.bumpDir==='right'? 1 : 0);
             const by = this.state.player.pos.y + (this.bumpDir==='up'? -1 : this.bumpDir==='down'? 1 : 0);
             if (bx === x && by === y) isBumped = true;
          }
          
          if (isBumped && !this.settings.reduceMotion) {
             this.mapGraphics.fillStyle(0x6666ff, 1); 
             this.mapGraphics.fillRoundedRect(px+1, py+1, TILE-2, TILE-2, 4);
             this.mapGraphics.lineStyle(2, 0xaaaaff, 1);
             this.mapGraphics.strokeRoundedRect(px+1, py+1, TILE-2, TILE-2, 4);
          } else {
             let wallColor = parseInt(biome.palette.walls.replace('#', '0x'), 16);
             if (this.settings.highContrast) wallColor = 0xffffff;
             this.mapGraphics.fillStyle(wallColor, this.settings.highContrast ? 1 : 0.4); 
             this.mapGraphics.fillRoundedRect(px+2, py+2, TILE-4, TILE-4, 4);
             this.mapGraphics.lineStyle(2, wallColor, 1);
             this.mapGraphics.strokeRoundedRect(px+2, py+2, TILE-4, TILE-4, 4);
          }
        }
        else if (t === '.') { 
          const pCol = parseInt(biome.palette.pellets.replace('#', '0x'), 16); 
          this.mapGraphics.fillStyle(pCol, 0.4); 
          if (this.settings.colorblindMode) {
             this.mapGraphics.fillRect(px+TILE/2 - 2, py+TILE/2 - 2, 4, 4);
          } else {
             this.mapGraphics.fillCircle(px+TILE/2, py+TILE/2, 3); 
          }
        }
        else if (t === 'o') { 
          const pCol = parseInt(biome.palette.pellets.replace('#', '0x'), 16); 
          this.mapGraphics.fillStyle(pCol, 0.8); 
          if (this.settings.colorblindMode) {
             this.mapGraphics.fillStyle(pCol, 1);
             this.mapGraphics.fillRect(px+TILE/2 - 6, py+TILE/2 - 6, 12, 12);
             this.mapGraphics.fillStyle(0x000000, 1);
             this.mapGraphics.fillRect(px+TILE/2 - 3, py+TILE/2 - 3, 6, 6);
          } else {
             this.mapGraphics.fillCircle(px+TILE/2, py+TILE/2, 7); 
          }
        }
        else if (t === 'T') { this.mapGraphics.fillStyle(0xffaa00, 0.6); this.mapGraphics.fillTriangle(px+TILE/2, py+4, px+4, py+TILE-4, px+TILE-4, py+TILE-4); }
        else if (t === 'E') { this.mapGraphics.fillStyle(0xff00aa, 0.8); this.mapGraphics.fillRect(px+8, py+8, TILE-16, TILE-16); }
      }
    }
  }

  private redraw(): void {
    this.fxGraphics.clear();
    this.entityGraphics.clear();
    
    // Draw trails
    for (const t of this.trails) {
      this.fxGraphics.fillStyle(0xfff200, t.alpha * 0.5);
      this.fxGraphics.fillCircle(t.x, t.y, 10 * t.scale);
    }

    // Draw particles
    for (const p of this.particles) {
      const alpha = Math.max(0, p.life / p.maxLife);
      this.fxGraphics.fillStyle(p.color, alpha);
      const s = p.size * alpha; // shrink as they die
      if (p.shape === 'circle') {
        this.fxGraphics.fillCircle(p.x, p.y, s / 2);
      } else {
        this.fxGraphics.fillRect(p.x - s / 2, p.y - s / 2, s, s);
      }
    }

    let px = this.state.player.pos.x * TILE;
    let py = this.state.player.pos.y * TILE;
    
    if (this.bumpFeedback > 0 && !this.settings.reduceMotion) {
      const shakeAmt = (this.bumpFeedback / 100) * 4;
      if (this.bumpDir === 'left') px -= shakeAmt;
      else if (this.bumpDir === 'right') px += shakeAmt;
      else if (this.bumpDir === 'up') py -= shakeAmt;
      else if (this.bumpDir === 'down') py += shakeAmt;
    }
    
    // Power duration visual feedback (blinking or blue)
    let isFrightened = this.state.powerTicks > 0;

    this.entityGraphics.fillStyle(0xFFD700, 1); 
    if (this.settings.colorblindMode) {
        // Draw Pacman as a wedge/pacman shape or triangle pointing
        this.entityGraphics.fillTriangle(px+TILE/2 + 12, py+TILE/2, px+TILE/2 - 10, py+TILE/2 - 10, px+TILE/2 - 10, py+TILE/2 + 10);
    } else {
        this.entityGraphics.fillCircle(px+TILE/2, py+TILE/2, 12);
    }
    
    for (const e of this.state.enemies) { 
      let eColor = 0x39FF14; // Default: Toxic Green
      if (e.kind === 'hunter') eColor = 0xFF003C; // Crimson
      else if (e.kind === 'boss' || e.kind === 'splitter') eColor = 0xFF00FF; // Magenta
      
      // Panic feedback
      if (isFrightened && e.kind !== 'boss') {
        const timeBlink = (Date.now() % 400 < 200) && this.state.powerTicks < 15;
        eColor = timeBlink ? 0xffffff : 0x0055ff; // flashing blue/white
      }

      this.entityGraphics.fillStyle(eColor, 1); 
      if (this.settings.colorblindMode) {
        if (isFrightened && e.kind !== 'boss') {
           // Vulnerable ghost = diamond
           this.entityGraphics.beginPath();
           this.entityGraphics.moveTo(e.pos.x*TILE+TILE/2, e.pos.y*TILE+TILE/2 - 12);
           this.entityGraphics.lineTo(e.pos.x*TILE+TILE/2 + 12, e.pos.y*TILE+TILE/2);
           this.entityGraphics.lineTo(e.pos.x*TILE+TILE/2, e.pos.y*TILE+TILE/2 + 12);
           this.entityGraphics.lineTo(e.pos.x*TILE+TILE/2 - 12, e.pos.y*TILE+TILE/2);
           this.entityGraphics.closePath();
           this.entityGraphics.fillPath();
        } else if (e.kind === 'hunter') {
           // Hunter = square
           this.entityGraphics.fillRect(e.pos.x*TILE+TILE/2 - 10, e.pos.y*TILE+TILE/2 - 10, 20, 20);
        } else {
           // Normal = hexagon (approx) or circle with dot
           this.entityGraphics.fillCircle(e.pos.x*TILE+TILE/2, e.pos.y*TILE+TILE/2, 12); 
           this.entityGraphics.fillStyle(0x000000, 1);
           this.entityGraphics.fillCircle(e.pos.x*TILE+TILE/2, e.pos.y*TILE+TILE/2, 4); 
        }
      } else {
        this.entityGraphics.fillCircle(e.pos.x*TILE+TILE/2, e.pos.y*TILE+TILE/2, 12); 
      }
    }
    
    this.cameraTarget.setPosition(this.state.player.pos.x * TILE + TILE/2, this.state.player.pos.y * TILE + TILE/2);
  }
}
